import React from "react"
import {ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

import {THeaderCellProps} from "../../../components/Table/HeaderCell.tsx"
import {EOrderDirection, TColumns} from "../../../components/Table/utils/shared.ts"
import {combineRef} from "../../../utils/ref.ts"

/**
 * The leads table's light header cell: a small grey label that sorts on click when the column is sortable.
 * Columns are neither dragged nor pinned here.
 */
export function LeadsHeaderCell<TCol extends TColumns, TRowData extends Record<string, any>>({
  columnMeta,
  onOrder,
  ref,
  colIndex,
  orderDirection,
}: THeaderCellProps<TCol, TRowData>): React.ReactNode {
  const Value = columnMeta.HeaderCellValue
  const label = Value ? <Value columnMeta={columnMeta} /> : columnMeta.column
  const SortIcon =
    orderDirection === EOrderDirection.ASC
      ? ChevronUpIcon
      : orderDirection === EOrderDirection.DESC
        ? ChevronDownIcon
        : ChevronUpDownIcon

  return (
    <div
      ref={combineRef(ref)}
      role={"columnheader"}
      aria-sort={onOrder ? ariaSort(orderDirection) : undefined}
      className={twMerge(
        "sticky top-0 z-15 flex items-center border-b border-cr-slate-15 bg-cr-slate-1 text-xs font-semibold whitespace-nowrap text-cr-slate-60",
        !onOrder && "px-2.5 py-2.5",
        columnMeta.headerCellClassName
      )}
      style={{gridRow: 1, gridColumn: colIndex + 1}}
    >
      {onOrder ? (
        <button
          type={"button"}
          onClick={onOrder}
          className={twMerge(
            "flex w-full items-center gap-[5px] px-2.5 py-2.5 text-left transition-colors hover:text-cr-blue-dark",
            orderDirection && "text-cr-blue hover:text-cr-blue"
          )}
        >
          {label}
          <SortIcon
            aria-hidden={"true"}
            className={twMerge("size-3.5 shrink-0", orderDirection ? "opacity-100" : "opacity-35")}
          />
        </button>
      ) : (
        label
      )}
    </div>
  )
}

const ariaSort = (direction: EOrderDirection | null) =>
  direction === EOrderDirection.ASC ? "ascending" : direction === EOrderDirection.DESC ? "descending" : "none"
